import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { apiGet } from "@/lib/apiClient";

type TrialPlanResponse = {
  isTrial?: boolean;
  trialExpired?: boolean;
  daysUntilTrialDeletion?: number | null;
  trialDeletionEligible?: boolean;
};

export function TrialExpiredNotice() {
  const [expired, setExpired] = useState(false);
  const [daysUntilDeletion, setDaysUntilDeletion] = useState<number | null>(null);
  const [deletionEligible, setDeletionEligible] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await apiGet<TrialPlanResponse>("/api/billing/me/plan");
      if (!active || !res.ok || !res.data) return;
      const payload = res.data;
      setExpired(payload.isTrial === true && payload.trialExpired === true);
      setDaysUntilDeletion(
        typeof payload.daysUntilTrialDeletion === "number"
          ? payload.daysUntilTrialDeletion
          : null,
      );
      setDeletionEligible(payload.trialDeletionEligible === true);
    })();
    return () => {
      active = false;
    };
  }, []);

  if (!expired) return null;

  const countdownText = deletionEligible
    ? "Account is now eligible for deletion."
    : typeof daysUntilDeletion === "number"
      ? `Account deletion in ${daysUntilDeletion} day${daysUntilDeletion === 1 ? "" : "s"} unless upgraded.`
      : "Account deletion countdown is unavailable.";

  return (
    <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900 dark:border-rose-400/40 dark:bg-rose-500/10 dark:text-rose-100">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
        <div className="leading-relaxed">
          <p>
            Trial has ended. Upgrade your plan to continue creating or editing records. You can
            still add and manage users.
          </p>
          <p className="font-semibold">{countdownText}</p>
        </div>
      </div>
      <Link
        to="/organization"
        className="shrink-0 rounded-md bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-500"
      >
        Manage Subscription
      </Link>
    </div>
  );
}
